/**
 * Overpass API 服務
 * 用於查詢 OpenStreetMap 中的巴士站信息
 */

import { calculateDistance } from './geolocation';

// Overpass API 端點
const OVERPASS_API_URL = 'https://overpass-api.de/api/interpreter';

// 請求緩存
const requestCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5分鐘緩存

/**
 * 生成緩存鍵
 * @param {string} query - 查詢字符串
 * @returns {string} 緩存鍵
 */
const generateCacheKey = (query) => {
  // 使用 encodeURIComponent 處理中文字符，然後轉為 base64
  try {
    const encoded = encodeURIComponent(query);
    return btoa(encoded).replace(/[^a-zA-Z0-9]/g, '');
  } catch (error) {
    // 如果仍然失敗，使用簡單的哈希方法
    let hash = 0;
    for (let i = 0; i < query.length; i++) {
      const char = query.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }
};

/**
 * 檢查緩存是否有效
 * @param {string} key - 緩存鍵
 * @returns {boolean} 是否有效
 */
const isCacheValid = (key) => {
  const cached = requestCache.get(key);
  if (!cached) return false;
  return Date.now() - cached.timestamp < CACHE_DURATION;
};

/**
 * 構建 Overpass QL 查詢語句（優化版本，只查詢 100m 內）
 * @param {number} lat - 緯度
 * @param {number} lon - 經度
 * @param {number} radius - 搜索半徑（米），默認 100 米
 * @returns {string} Overpass QL 查詢語句
 */
const buildOverpassQuery = (lat, lon, radius = 100) => {
  return `
    [out:json][timeout:15];
    (
      // 只查詢 node 類型的巴士站，減少查詢複雜度
      node["public_transport"="platform"]["bus"="yes"](around:${radius},${lat},${lon});
      node["highway"="bus_stop"](around:${radius},${lat},${lon});
      node["amenity"="bus_station"](around:${radius},${lat},${lon});
    );
    out meta;
  `;
};

/**
 * 查詢附近的巴士站（優化版本，帶緩存）
 * @param {number} latitude - 緯度
 * @param {number} longitude - 經度
 * @param {number} radius - 搜索半徑（米），默認 100 米
 * @returns {Promise<Array>} 巴士站列表
 */
export const searchNearbyBusStops = async (latitude, longitude, radius = 100) => {
  try {
    const query = buildOverpassQuery(latitude, longitude, radius);
    const cacheKey = generateCacheKey(query);
    
    // 檢查緩存
    if (isCacheValid(cacheKey)) {
      console.log('使用緩存的巴士站數據');
      const cached = requestCache.get(cacheKey);
      return cached.data.map(stop => ({
        ...stop,
        distance: calculateDistance(latitude, longitude, stop.latitude, stop.longitude)
      })).sort((a, b) => a.distance - b.distance);
    }
    
    console.log(`查詢 ${radius}m 內的巴士站...`);
    
    const response = await fetch(OVERPASS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(query)}`
    });

    if (!response.ok) {
      throw new Error(`Overpass API 請求失敗: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.elements || !Array.isArray(data.elements)) {
      throw new Error('Overpass API 返回數據格式錯誤');
    }

    // 處理和格式化巴士站數據
    const busStops = data.elements
      .map(element => processBusStopElement(element, latitude, longitude))
      .filter(stop => stop !== null) // 過濾無效數據
      .sort((a, b) => a.distance - b.distance); // 按距離排序

    // 緩存結果
    requestCache.set(cacheKey, {
      data: busStops,
      timestamp: Date.now()
    });
    
    console.log(`找到 ${busStops.length} 個巴士站`);
    return busStops;
  } catch (error) {
    console.error('查詢巴士站時發生錯誤:', error);
    throw new Error(`查詢巴士站失敗: ${error.message}`);
  }
};

/**
 * 處理 Overpass API 返回的元素數據
 * @param {Object} element - Overpass API 元素
 * @param {number} userLat - 用戶緯度
 * @param {number} userLon - 用戶經度
 * @returns {Object|null} 處理後的巴士站數據
 */
const processBusStopElement = (element, userLat, userLon) => {
  try {
    let lat, lon, name, id;

    // 處理不同類型的元素 (node, way, relation)
    if (element.type === 'node') {
      lat = element.lat;
      lon = element.lon;
      id = element.id;
    } else if (element.type === 'way' || element.type === 'relation') {
      // 對於 way 和 relation，使用 center 坐標
      if (element.center) {
        lat = element.center.lat;
        lon = element.center.lon;
      } else {
        return null; // 沒有中心坐標，跳過
      }
      id = element.id;
    } else {
      return null; // 不支援的元素類型
    }

    // 提取名稱
    name = element.tags?.name || 
           element.tags?.['name:zh'] || 
           element.tags?.['name:zh-TW'] || 
           element.tags?.['name:zh-CN'] ||
           element.tags?.ref ||
           `巴士站 ${id}`;

    // 計算距離
    const distance = calculateDistance(userLat, userLon, lat, lon);

    // 提取其他有用信息
    const tags = element.tags || {};
    
    return {
      id: `osm_${element.type}_${id}`,
      name,
      latitude: lat,
      longitude: lon,
      distance,
      type: element.type,
      tags: {
        ref: tags.ref, // 站台編號
        operator: tags.operator, // 運營商
        network: tags.network, // 網絡
        public_transport: tags.public_transport,
        bus: tags.bus,
        wheelchair: tags.wheelchair, // 無障礙設施
        shelter: tags.shelter, // 候車亭
        bench: tags.bench, // 座椅
        covered: tags.covered, // 有遮蓋
        lit: tags.lit, // 照明
        route_ref: tags.route_ref, // 路線編號
        ...tags
      },
      osmId: id,
      osmType: element.type
    };
  } catch (error) {
    console.warn('處理巴士站元素時發生錯誤:', error, element);
    return null;
  }
};

/**
 * 根據地址或地名搜索巴士站（優化版本，帶緩存）
 * @param {string} query - 搜索查詢
 * @param {number} limit - 結果數量限制，默認 20
 * @returns {Promise<Array>} 巴士站列表
 */
export const searchBusStopsByQuery = async (query, limit = 20) => {
  try {
    if (!query || !query.trim()) {
      return [];
    }

    // 檢查緩存
    const cacheKey = generateCacheKey(`search_${query}_${limit}`);
    if (isCacheValid(cacheKey)) {
      console.log('使用緩存的搜索結果');
      return requestCache.get(cacheKey).data;
    }
    
    // 香港地區邊界框 (bounding box)
    // Overpass bbox 格式: (south,west,north,east)
    // 緯度範圍: 22.15 到 22.58 (south to north)
    // 經度範圍: 113.83 到 114.51 (west to east)
    const hkBbox = '22.15,113.83,22.58,114.51';
    
    // 清理和轉義查詢字符串
    // Overpass 正則表達式需要特殊處理
    const cleanQuery = query.trim();
    
    // 如果查詢為空，返回空結果
    if (!cleanQuery) {
      return [];
    }
    
    // 轉義特殊字符，但保留中文字符
    // Overpass 正則表達式特殊字符需要轉義
    // 特殊字符: . * + ? ^ $ { } [ ] \ | ( )
    // 注意：對於中文字符，Overpass 應該能正確處理，但需要正確轉義特殊字符
    let escapedQuery = cleanQuery;
    
    // 只轉義真正需要轉義的字符
    // 使用更保守的轉義策略，避免破壞中文字符
    const specialChars = /[.*+?^${}()[\]\\|]/g;
    escapedQuery = escapedQuery.replace(specialChars, '\\$&');
    
    // Overpass 的 ~ 操作符支持部分匹配，不需要添加 .*
    // 直接使用轉義後的查詢字符串
    const searchPattern = escapedQuery;
    
    // 使用更簡單的查詢策略，分別搜索不同的標籤
    // 限制在香港地區內，使用 bbox 過濾
    const overpassQuery = `[out:json][timeout:25];
(
  node["public_transport"="platform"]["bus"="yes"](bbox:${hkBbox})["name"~"${searchPattern}",i];
  node["highway"="bus_stop"](bbox:${hkBbox})["name"~"${searchPattern}",i];
  node["amenity"="bus_station"](bbox:${hkBbox})["name"~"${searchPattern}",i];
  node["public_transport"="platform"]["bus"="yes"](bbox:${hkBbox})["name:zh"~"${searchPattern}",i];
  node["highway"="bus_stop"](bbox:${hkBbox})["name:zh"~"${searchPattern}",i];
  node["amenity"="bus_station"](bbox:${hkBbox})["name:zh"~"${searchPattern}",i];
  node["public_transport"="platform"]["bus"="yes"](bbox:${hkBbox})["name:en"~"${searchPattern}",i];
  node["highway"="bus_stop"](bbox:${hkBbox})["name:en"~"${searchPattern}",i];
  node["amenity"="bus_station"](bbox:${hkBbox})["name:en"~"${searchPattern}",i];
  node["public_transport"="platform"]["bus"="yes"](bbox:${hkBbox})["ref"~"${searchPattern}",i];
  node["highway"="bus_stop"](bbox:${hkBbox})["ref"~"${searchPattern}",i];
);
out meta;`;

    console.log('搜索巴士站:', query);
    console.log('清理後的查詢:', cleanQuery);
    console.log('轉義後的查詢:', escapedQuery);
    console.log('Overpass 查詢:', overpassQuery);

    const response = await fetch(OVERPASS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(overpassQuery)}`
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Overpass API 錯誤響應:', errorText);
      console.error('查詢語句:', overpassQuery);
      
      // 如果是 400 錯誤，可能是查詢語法問題
      if (response.status === 400) {
        throw new Error(`查詢語法錯誤，請嘗試使用英文名稱或站台編號搜索`);
      }
      
      throw new Error(`Overpass API 請求失敗: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.elements || !Array.isArray(data.elements)) {
      console.error('Overpass API 返回數據格式錯誤:', data);
      throw new Error('Overpass API 返回數據格式錯誤');
    }

    console.log(`找到 ${data.elements.length} 個匹配的巴士站元素`);

    // 處理結果，去重並只取前 limit 個
    const seenIds = new Set();
    const busStops = data.elements
      .map(element => {
        // 去重：使用 OSM ID
        const osmId = `${element.type}_${element.id}`;
        if (seenIds.has(osmId)) {
          return null;
        }
        seenIds.add(osmId);
        return processBusStopElement(element, 0, 0);
      })
      .filter(stop => stop !== null)
      .slice(0, limit);

    console.log(`處理後得到 ${busStops.length} 個巴士站`);

    // 緩存結果
    requestCache.set(cacheKey, {
      data: busStops,
      timestamp: Date.now()
    });

    return busStops;
  } catch (error) {
    console.error('搜索巴士站時發生錯誤:', error);
    // 如果是 Overpass API 的錯誤，返回更友好的錯誤信息
    if (error.message.includes('timeout') || error.message.includes('rate limit')) {
      throw new Error('搜索請求超時或達到限制，請稍後再試或嘗試使用地圖搜索');
    }
    throw new Error(`搜索巴士站失敗: ${error.message}`);
  }
};

/**
 * 獲取巴士站詳細信息
 * @param {string} osmId - OpenStreetMap ID
 * @param {string} osmType - OpenStreetMap 類型 (node, way, relation)
 * @returns {Promise<Object>} 巴士站詳細信息
 */
export const getBusStopDetails = async (osmId, osmType) => {
  try {
    const query = `
      [out:json][timeout:25];
      ${osmType}(${osmId});
      out meta;
    `;

    const response = await fetch(OVERPASS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(query)}`
    });

    if (!response.ok) {
      throw new Error(`Overpass API 請求失敗: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.elements || data.elements.length === 0) {
      throw new Error('未找到指定的巴士站');
    }

    return processBusStopElement(data.elements[0], 0, 0);
  } catch (error) {
    console.error('獲取巴士站詳細信息時發生錯誤:', error);
    throw new Error(`獲取巴士站詳細信息失敗: ${error.message}`);
  }
};

/**
 * 檢查 Overpass API 服務狀態（優化版本）
 * @returns {Promise<boolean>} 服務是否可用
 */
export const checkOverpassApiStatus = async () => {
  try {
    const cacheKey = 'api_status_check';
    if (isCacheValid(cacheKey)) {
      return requestCache.get(cacheKey).data;
    }
    
    const testQuery = `
      [out:json][timeout:5];
      node["amenity"="bus_station"](around:100,0,0);
      out count;
    `;

    const response = await fetch(OVERPASS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(testQuery)}`
    });

    const isOk = response.ok;
    
    // 緩存狀態檢查結果（較短時間）
    requestCache.set(cacheKey, {
      data: isOk,
      timestamp: Date.now()
    });
    
    return isOk;
  } catch (error) {
    console.warn('Overpass API 狀態檢查失敗:', error);
    return false;
  }
};

/**
 * 清除緩存
 */
export const clearCache = () => {
  requestCache.clear();
  console.log('Overpass API 緩存已清除');
};
